import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Cpu, AlertTriangle } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AdminLlmUsage() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  const [cfg, setCfg] = useState({ cost_per_call_cents: 2, daily_call_alert: 0, daily_cost_alert_cents: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (dys) => {
    try {
      const { data } = await api.get("/admin/llm-usage", { params: { days: dys } });
      setD(data);
      setCfg({
        cost_per_call_cents: data.config.cost_per_call_cents,
        daily_call_alert: data.config.daily_call_alert,
        daily_cost_alert_cents: data.config.daily_cost_alert_cents,
      });
    } catch (e) { logger.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(days); }, [load, days]);

  const saveCfg = async () => {
    setSaving(true);
    try {
      await api.put("/admin/llm-usage/config", {
        cost_per_call_cents: Number(cfg.cost_per_call_cents),
        daily_call_alert: Number(cfg.daily_call_alert),
        daily_cost_alert_cents: Number(cfg.daily_cost_alert_cents),
      });
      toast.success("Cost settings saved.");
      load(days);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save."); }
    finally { setSaving(false); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const maxDay = Math.max(1, ...d.by_day.map((x) => x.calls));
  const stats = [
    { label: "Total calls", value: d.total_calls, sub: `last ${d.days}d` },
    { label: "Today", value: d.today_calls, sub: money(d.today_cost_cents) },
    { label: "Est. cost", value: money(d.est_cost_cents), sub: `@ ${d.config.cost_per_call_cents}¢/call` },
    { label: "Success", value: `${d.success_rate}%`, sub: `${d.fallback_rate}% fallback` },
    { label: "Avg latency", value: `${d.avg_latency_ms}ms` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-llm-usage">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Cpu className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> LLM Usage & Cost</h1>
          <p className="text-muted-foreground text-sm">Call volume, latency and estimated cost. Cost is an estimate based on your per-call rate.</p>
        </div>
        <div className="flex gap-1 bg-zinc-100 rounded-lg p-0.5" data-testid="llm-usage-range">
          {[[7, "7d"], [30, "30d"], [90, "90d"]].map(([v, l]) => (
            <button key={v} onClick={() => setDays(v)} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${days === v ? "bg-white shadow-sm text-zinc-900" : "text-muted-foreground hover:text-zinc-700"}`} data-testid={`llm-range-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      {d.alerts.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="llm-alerts">
          {d.alerts.map((a) => <p key={a} className="flex items-center gap-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4" /> {a}</p>)}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-zinc-200"><CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className="font-heading text-2xl font-bold mt-1" data-testid={`llm-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{s.value}</p>
            {s.sub && <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>}
          </CardContent></Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Calls per day</p>
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            {d.by_day.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="llm-byday-empty">No LLM calls recorded yet.</p> : (
              <div className="flex items-end gap-1 h-32" data-testid="llm-byday-chart">
                {d.by_day.map((x) => (
                  <div key={x.day} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${x.day}: ${x.calls}`}>
                    <div className="w-full bg-[#24468C] rounded-t" style={{ height: `${(x.calls / maxDay) * 100}%`, minHeight: "2px" }} />
                    <span className="text-[9px] text-muted-foreground">{x.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By model</p>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {d.by_model.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="llm-bymodel-empty">No data.</p> : (
              <table className="w-full text-sm" data-testid="llm-bymodel-table">
                <thead><tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Model</th><th className="px-4 py-2.5 font-semibold text-right">Calls</th><th className="px-4 py-2.5 font-semibold text-right">Est. cost</th>
                </tr></thead>
                <tbody>{d.by_model.map((m) => (
                  <tr key={m.model} className="border-b border-zinc-100"><td className="px-4 py-2.5 font-mono text-xs">{m.model}</td><td className="px-4 py-2.5 text-right">{m.calls}</td><td className="px-4 py-2.5 text-right">{money(m.est_cost_cents)}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cost & alert settings</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Cost per call (¢)</Label>
            <Input type="number" step="0.1" min="0" value={cfg.cost_per_call_cents} onChange={(e) => setCfg({ ...cfg, cost_per_call_cents: e.target.value })} data-testid="llm-cost-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Daily call alert (0=off)</Label>
            <Input type="number" min="0" value={cfg.daily_call_alert} onChange={(e) => setCfg({ ...cfg, daily_call_alert: e.target.value })} data-testid="llm-call-alert-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Daily cost alert (¢, 0=off)</Label>
            <Input type="number" min="0" value={cfg.daily_cost_alert_cents} onChange={(e) => setCfg({ ...cfg, daily_cost_alert_cents: e.target.value })} data-testid="llm-cost-alert-input" /></div>
        </div>
        <Button onClick={saveCfg} disabled={saving} data-testid="llm-cost-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save settings</Button>
      </div>
    </div>
  );
}
